/**
 * Bulk-create — v1 trip → `ParsedOrder[]` prefill mapper.
 *
 * Lets an admin paste a v1 trip's JSON payload and push it straight into the
 * review phase of the bulk-create flow (via `BulkCreateFormHandle.prefillRows`).
 * Payload-based only: no v1-prod connection, no server changes — the admin
 * supplies the JSON. This flow is DELIVERY-only, so each v1 order maps to one
 * `ParsedOrder` built from its `deliveryAddress` + first parcel.
 *
 * Pure module: no React/UI dependency.
 */
import { formatCanadianPhone, makeOrderId } from "./parse";
import type { ParsedOrder } from "./types";

/** A v1 address block as it appears in a pasted trip payload. */
export interface V1Address {
  line1: string;
  line2?: string | null;
  city: string;
  province: string;
  country: string;
  postalCode?: string | null;
  location?: { lat: number; lng: number } | null;
}

/** A v1 parcel: weight in kg, dimensions in cm. */
export interface V1Parcel {
  weight: number;
  dimensions: { length: number; width: number; height: number };
}

/** A single order inside a v1 trip payload. */
export interface V1OrderPayload {
  id: string;
  senderName: string;
  senderEmail?: string | null;
  senderPhone?: string | null;
  recipientName: string;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  deliveryAddress?: V1Address | null;
  pickupAddress?: V1Address | null;
  parcels: V1Parcel[];
  /** Free-text delivery note, when the v1 order carries one. */
  deliveryNotes?: string | null;
}

/** The full v1 trip payload an admin pastes. */
export interface V1TripPayload {
  id: string;
  orders: V1OrderPayload[];
}

/** Coerce a nullable string field to a plain "" (the ParsedOrder contract). */
const str = (value: string | null | undefined): string => value ?? "";

/**
 * Map one v1 order's DELIVERY leg to a `ParsedOrder`. Throws if the order has
 * no `deliveryAddress` — this flow can't create a delivery without one.
 */
const mapV1Order = (order: V1OrderPayload, index: number): ParsedOrder => {
  const delivery = order.deliveryAddress;
  if (!delivery) {
    throw new Error(
      `v1 order "${order.id}" has no deliveryAddress — cannot import as a delivery`
    );
  }

  const parcel = order.parcels[0];

  return {
    id: makeOrderId(index),
    recipientName: str(order.recipientName),
    recipientPhone: formatCanadianPhone(str(order.recipientPhone)),
    recipientEmail: str(order.recipientEmail),
    recipientAddress: str(delivery.line1),
    recipientUnit: str(delivery.line2),
    recipientCity: str(delivery.city),
    recipientProvince: str(delivery.province),
    recipientPostalCode: str(delivery.postalCode),
    // v1 parcels are canonical kg / cm. Fall back to the same defaults the
    // manual-entry row uses when a parcel is absent.
    weight: parcel?.weight ?? 1,
    weightUnit: "kg",
    length: parcel?.dimensions.length ?? 30,
    width: parcel?.dimensions.width ?? 20,
    height: parcel?.dimensions.height ?? 15,
    dimensionUnit: "cm",
    deliveryNotes: str(order.deliveryNotes),
    status: "pending",
    selected: true,
  };
};

/**
 * Map a v1 trip payload to bulk-create rows — one `ParsedOrder` per v1 order's
 * delivery. Throws (via `mapV1Order`) on an order missing a delivery address.
 */
export const mapV1TripToRows = (trip: V1TripPayload): ParsedOrder[] =>
  trip.orders.map(mapV1Order);

/**
 * Parse + minimally validate a pasted v1 trip JSON string. Throws a clear Error
 * on malformed JSON or a payload that isn't shaped like a trip — the caller
 * surfaces the message as a toast.
 */
export const parseV1TripJson = (text: string): V1TripPayload => {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON — could not parse the pasted text.");
  }

  if (typeof data !== "object" || data === null) {
    throw new Error("Expected a v1 trip object.");
  }

  const trip = data as Record<string, unknown>;
  if (typeof trip.id !== "string") {
    throw new Error('Missing or invalid trip "id".');
  }
  if (!Array.isArray(trip.orders)) {
    throw new Error('Missing or invalid trip "orders" array.');
  }

  return trip as unknown as V1TripPayload;
};
